import React from "react";
import {
  Instagram,
  Linkedin,
  Mail,
  Phone,
  MapPin,
  ArrowUpRight,
} from "lucide-react";
import { VIRTUAL_BRANDS, NAV_ITEMS } from "../mock/data";

const Footer = () => {
  return (
    <footer className="relative bg-[#050507] text-white pt-16 pb-8 border-t border-white/8 overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-25" />

      <div className="relative max-w-[1480px] mx-auto px-6 lg:px-10">
        {/* Final CTA banner */}
        <div className="bg-[#14E0BF] rounded-[32px] p-8 lg:p-14 mb-20 relative overflow-hidden">
          <div className="absolute inset-0 stripe-overlay opacity-30" />
          <div className="relative grid lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#07070A]/65 mb-3">
                Ready when you are
              </div>
              <h3 className="text-[#07070A]">
                <span className="font-display text-4xl lg:text-6xl uppercase leading-[0.95] block">
                  Britain&rsquo;s 1st Indian
                </span>
                <span className="font-serif-display italic text-4xl lg:text-6xl leading-[0.95] block mt-1">
                  virtual kitchen franchise.
                </span>
              </h3>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <a
                href="#journey"
                className="btn-pill inline-flex items-center gap-2 bg-[#07070A] text-white font-display text-[13px] uppercase tracking-wider px-7 py-4 rounded-full"
              >
                Apply Now
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Wordmark */}
        <div className="border-b border-white/10 pb-12 mb-14">
          <h2 className="font-display text-[18vw] lg:text-[230px] leading-[0.85] lowercase tracking-tight">
            <span className="text-[#14E0BF]">k</span>itchn<span className="text-[#14E0BF]">worx</span>
          </h2>
          <p className="font-serif-display italic text-2xl lg:text-3xl text-white/65 mt-4 max-w-[640px]">
            UK&rsquo;s 1st Indian virtual kitchen franchise. We build the brands, you cook the food, the world places the order.
          </p>
        </div>

        {/* Columns */}
        <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-4">
            <div className="font-display text-2xl mb-6 flex items-baseline">
              <span className="text-[#14E0BF] mr-1">k</span>itchn<span className="text-[#14E0BF]">worx</span>
            </div>
            <div className="space-y-3 text-white/65 text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 shrink-0 text-[#14E0BF]" />
                <span>Unit F, Mount Avenue, Bletchley, MK1 1LS, United Kingdom</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 shrink-0 text-[#14E0BF]" />
                <a href="tel:+447404522889" className="hover:text-white transition-colors">
                  07404 522889
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 shrink-0 text-[#14E0BF]" />
                <a
                  href="mailto:hello@kitchnworx.co.uk"
                  className="hover:text-white transition-colors"
                >
                  hello@kitchnworx.co.uk
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] mb-5 text-[#14E0BF]">
              Navigate
            </div>
            <ul className="space-y-3 text-sm text-white/65">
              {NAV_ITEMS.map((n) => (
                <li key={n.label}>
                  <a href={n.href} className="hover:text-white transition-colors">
                    {n.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] mb-5 text-[#14E0BF]">
              Brands
            </div>
            <ul className="space-y-3 text-sm text-white/65">
              {VIRTUAL_BRANDS.map((b) => (
                <li key={b.id}>
                  <a
                    href={`#brand-${b.id}`}
                    className="hover:text-white transition-colors flex items-center justify-between group"
                  >
                    <span>{b.name}</span>
                    <span
                      className="font-mono text-[10px] opacity-60 group-hover:opacity-100"
                      style={{ color: b.accent }}
                    >
                      →
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] mb-5 text-[#14E0BF]">
              Follow
            </div>
            <div className="flex items-center gap-3">
              {[Instagram, Linkedin].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-10 h-10 rounded-full border border-white/15 hover:bg-[#14E0BF] hover:text-[#07070A] hover:border-[#14E0BF] flex items-center justify-center transition-colors"
                  aria-label="social"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-white/40 text-xs font-mono uppercase tracking-wider">
          <p>
            © {new Date().getFullYear()} kitchnworx Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-[#14E0BF]">Privacy</a>
            <a href="#" className="hover:text-[#14E0BF]">Terms</a>
            <a href="#" className="hover:text-[#14E0BF]">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
